import { denormalisedResponseEntities } from '../util/data';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import {
  getTransitionsNeedingProviderAttention,
  getTransitionsNeedingCustomerAttention,
} from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import {
  allusersQueryApi,
  CompleteTxn,
  usersQueryApi,
  getCoachNotificationCountApi,
  getCustomerNotificationCountApi,
  nativeMeApi,
} from '../util/api';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CUSTOMER_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CUSTOMER_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CUSTOMER_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CUSTOMER_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CUSTOMER_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CUSTOMER_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowTimestamp: 0,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  customerUserNotificationCount: 0,
  customerUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
        currentUserShowTimestamp: payload ? new Date().getTime() : 0,
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        customerUserNotificationCount: 0,
        customerUserNotificationCountError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        currentUserNotificationCount: payload.transactions.length + (payload.nativeCount || 0),
      };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CUSTOMER_USER_NOTIFICATIONS_REQUEST:
      return { ...state, customerUserNotificationCountError: null };
    case FETCH_CUSTOMER_USER_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        customerUserNotificationCount: payload.transactions.length + (payload.nativeCount || 0),
      };
    case FETCH_CUSTOMER_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, customerUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.customerUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

export const getCustomerUserNotificationCount = state => {
  return state.user.customerUserNotificationCount;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = (transactions, nativeCount = 0) => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions, nativeCount },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

export const fetchCustomerUserNotificationsRequest = () => ({
  type: FETCH_CUSTOMER_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCustomerUserNotificationsSuccess = (transactions, nativeCount = 0) => ({
  type: FETCH_CUSTOMER_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions, nativeCount },
});

const fetchCustomerUserNotificationsError = e => ({
  type: FETCH_CUSTOMER_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

// Used to call sdk.ownListings.query() to ask Sharetribe whether this user
// has a published listing -- always fails now that Marketplace API
// credentials are revoked, which set currentUserHasListingsError on every
// single login/signup (this thunk runs unconditionally from fetchCurrentUser
// below), which is exactly what drives the Topbar's generic "Oh no,
// something went wrong" error banner. Found live: a real Google signup
// flashed that banner immediately after a fully successful login. The
// underlying answer ("is this user a coach") is already known -- it's the
// same authoritative Mongo-native isCoach flag syntheticSharetribeUser.js
// already stamps onto every currentUser response -- so this no longer needs
// a network call at all, live or otherwise.
export const fetchCurrentUserHasListings = () => (dispatch, getState) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;
  const isCoach = !!currentUser?.attributes?.profile?.publicData?.isCoach;
  dispatch(fetchCurrentUserHasListingsSuccess(isCoach));
  return Promise.resolve(null);
};

// Same root cause as fetchCurrentUserHasListings above: sdk.transactions.query()
// always fails now, tripping the same generic error banner for any
// unverified-email account (the only case this fires for). No Mongo-native
// equivalent already on hand here, so this degrades to the same "no orders"
// default fetchCurrentUserHasOrdersSuccess(false) already used for a logged
// -out user, instead of setting an error that blanks the Topbar with a scary
// banner over a value nothing currently reads urgently.
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => {
      log.error(e, 'fetch-current-user-has-orders-failed');
      dispatch(fetchCurrentUserHasOrdersSuccess(false));
    });
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

// Both notification thunks below queried live Sharetribe transactions
// (sdk.transactions.query) for the legacy "transactions needing this user's
// attention" list, alongside the already-native getCoachNotificationCountApi/
// getCustomerNotificationCountApi count. The Sharetribe half always fails
// now (credentials revoked) -- fetchCurrentUserNotifications' first query
// wasn't even inside its own try/catch, so it threw an uncaught rejection
// before the thunk got as far as its own request/error dispatch. Both now
// catch that failure and degrade to an empty legacy-transactions list with
// just the native count, instead of setting a *NotificationsError that
// feeds the Topbar's generic "Oh no, something went wrong" banner -- found
// live: this (plus the sibling fetchCurrentUserHasListings bug above) fired
// on every single login, including a real signup that had actually
// succeeded.
export const fetchCurrentUserNotifications = () => async (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  // fetchCurrentUser already populated this -- no need for a fresh
  // sdk.currentUser.show() round trip just to read the id.
  const currentUserId = getState().user?.currentUser?.id?.uuid;
  const customNotiQuery = {
    only: 'sale',
    providerId: currentUserId,
    meta_provider_notify: true,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());

  const nativeRes = await getCoachNotificationCountApi().catch(() => ({ count: 0 }));

  try {
    const [customRes, res] = await Promise.all([
      sdk.transactions.query(customNotiQuery),
      sdk.transactions.query(apiQueryParams),
    ]);
    const customTransaction = customRes?.data?.data || [];
    const transactions = [...new Set([...res.data.data, ...customTransaction])];
    dispatch(fetchCurrentUserNotificationsSuccess(transactions, nativeRes?.count || 0));
  } catch (e) {
    log.error(e, 'fetch-current-user-notifications-legacy-failed');
    dispatch(fetchCurrentUserNotificationsSuccess([], nativeRes?.count || 0));
  }
};

export const fetchCustomerUserNotifications = () => async (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingCustomerAttention();
  // fetchCurrentUser already populated this -- no need for a fresh
  // sdk.currentUser.show() round trip just to read the id.
  const currentUserId = getState().user?.currentUser?.id?.uuid;
  const customNotiQuery = {
    only: 'order',
    customerId: currentUserId,
    meta_customer_notify: true,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  const apiQueryParams = {
    only: 'order',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCustomerUserNotificationsRequest());

  const nativeRes = await getCustomerNotificationCountApi().catch(() => ({ count: 0 }));

  try {
    const [customResponse, regularResponse] = await Promise.all([
      sdk.transactions.query(customNotiQuery),
      sdk.transactions.query(apiQueryParams),
    ]);

    const customTransactions = customResponse?.data?.data || [];
    const regularTransactions = regularResponse?.data?.data || [];

    // Combine and deduplicate transactions
    const allTransactions = [...new Set([...regularTransactions, ...customTransactions])];

    dispatch(fetchCustomerUserNotificationsSuccess(allTransactions, nativeRes?.count || 0));
  } catch (e) {
    log.error(e, 'fetch-customer-user-notifications-legacy-failed');
    dispatch(fetchCustomerUserNotificationsSuccess([], nativeRes?.count || 0));
  }
};

/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve({});
  }
  // Set in-progress, no errors
  dispatch(currentUserShowRequest());

  // isAuthenticated comes from auth.duck's authInfo() thunk, which only
  // ever checks the Sharetribe SDK's own token/cookie -- a native-only
  // session (nativeLogin/nativeSignup, no Sharetribe cookie at all) always
  // reports isAuthenticated: false there, even with a perfectly valid
  // cf_session cookie. Skipping this fetch in that case meant a native
  // session's currentUser silently stayed null on every fresh page load
  // (hard navigation/refresh) -- native /me was simply never called to
  // find out -- which made every `auth: true` route (ProviderPage,
  // ProfileSettingsPage, ...) bounce a genuinely logged-in native user to
  // LoginPage. Confirmed live on staging. Only keep the short-circuit for
  // the legacy callParams path (CheckoutPage.duck.js/
  // PaymentMethodsPage.duck.js's stripeCustomer include), which calls
  // sdk.currentUser.show() directly and genuinely can't succeed without a
  // real Sharetribe session -- the native /me path below is cheap to
  // always attempt (a fast local 401 for a truly anonymous visitor, no
  // Sharetribe round trip) and is the only way to detect a native session.
  //
  // This short-circuit used to also clear currentUser to null, which was
  // correct back when isAuthenticated reliably meant "no session at all."
  // Now that isAuthenticated is always false for native sessions, that
  // wiped an already-correctly-populated native currentUser back to null
  // every time CheckoutPage's stripeCustomer fetch ran on mount -- Routes.js's
  // outer auth guard then saw currentUser: null and bounced a fully logged-in
  // user straight to SignupPage from the checkout page (confirmed live:
  // deterministic on every checkout page load, even after fixing
  // isUserAuthorized). Leave the existing currentUser in the store alone
  // instead of clobbering it; the impossible Sharetribe include simply never
  // resolves for this user, which is fine since native checkout doesn't
  // depend on it.
  if (!isAuthenticated && !afterLogin && callParams) {
    return Promise.resolve({});
  }

  // Phase 5 (native session): replaces the direct sdk.currentUser.show()
  // call for the common case. GET /api/auth/native/me is served by
  // requireAuthUser, which tries the native cf_session cookie first and
  // falls back to a real Sharetribe call otherwise -- either way it comes
  // back already shaped like a Sharetribe currentUser resource, so no
  // denormalisation step is needed there.
  //
  // callParams is still honored via the original direct-SDK path -- it's
  // only ever used by CheckoutPage.duck.js/PaymentMethodsPage.duck.js to
  // request `include: ['stripeCustomer.defaultPaymentMethod']`, a genuine
  // Sharetribe-specific saved-card feature the native /me endpoint doesn't
  // (and doesn't need to) know how to serve -- out of scope for Phase 5.
  const currentUserPromise = callParams
    ? sdk.currentUser.show(callParams).then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.show response');
        }
        return entities[0];
      })
    : nativeMeApi().then(response => {
        const currentUser = response?.currentUser;
        if (!currentUser) {
          throw new Error('Expected a currentUser in the native /me response');
        }
        return currentUser;
      });

  return currentUserPromise
    .then(currentUser => {
      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
          dispatch(fetchCustomerUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

export const usersQuery = async (params) => {
  const users = await usersQueryApi(params)
  return users
}

export const allusersQuery = async (params) => {
  const users = await allusersQueryApi(params)
  return users
}

export const completeTxnQuery = async (params) => {
  const txn = await CompleteTxn(params)
  return txn
}